import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthChange, getCurrentUser } from '../firebase/auth';
import { getUserData } from '../firebase/userData';
import { auth } from '../firebase/config';
import { isRawStripeSubscription, sanitizeSubscription } from '../utils/subscriptionHelpers';
import { logger } from '../utils/logger';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [uid, setUid] = useState(null);
  
  useEffect(() => {
    // Listen for authentication state changes
    const unsubscribe = onAuthChange(async (firebaseUser) => {
      if (firebaseUser) {
        // User is signed in
        // Claim free trial + profile only after email is verified
        if (firebaseUser.emailVerified) {
          try {
            const { claimUserProfile } = await import('../firebase/auth');
            await claimUserProfile();
          } catch (e) {
            logger.warn('claimUserProfile on auth change failed', e);
          }
        }

        // Get additional user data from Firestore (including subscription)
        const userData = await getUserData(firebaseUser.uid);
        
        // CRITICAL: Sanitize subscription data to prevent raw Stripe objects
        if (userData && userData.subscription) {
          if (isRawStripeSubscription(userData.subscription)) {
            logger.error('❌ CRITICAL: Raw Stripe subscription object detected in AuthContext!');
            // Remove corrupted subscription data - it will be synced on next page load
            userData.subscription = {
              status: 'expired',
              planId: 'expired',
              stripeCustomerId: null,
              stripeSubscriptionId: null
            };
          } else {
            // Sanitize subscription to ensure no nested objects
            userData.subscription = sanitizeSubscription(userData.subscription) || {
              status: 'expired',
              planId: 'expired',
              stripeCustomerId: null,
              stripeSubscriptionId: null
            };
          }
        }
        
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          emailVerified: firebaseUser.emailVerified,
          displayName: firebaseUser.displayName,
          ...userData
        });
        setIsAuthenticated(true);
        setUid(firebaseUser.uid);
      } else {
        // User is signed out
        setUser(null);
        setIsAuthenticated(false);
        setUid(null);
      }
      
      setLoading(false);
    });
    
    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);
  
  // Import auth functions
  const signup = async (email, password, userData) => {
    const { signup: signupFn } = await import('../firebase/auth');
    return await signupFn(email, password, userData);
  };
  
  const signin = async (email, password) => {
    const { signin: signinFn } = await import('../firebase/auth');
    return await signinFn(email, password);
  };
  
  const logout = async () => {
    const { logout: logoutFn } = await import('../firebase/auth');
    return await logoutFn();
  };
  
  const resetPassword = async (email) => {
    const { resetPassword: resetPasswordFn } = await import('../firebase/auth');
    return await resetPasswordFn(email);
  };
  
  const resendEmailVerification = async () => {
    const { resendEmailVerification: resendFn } = await import('../firebase/auth');
    return await resendFn();
  };
  
  const reloadUser = async () => {
    const { reloadUser: reloadFn, getCurrentUser } = await import('../firebase/auth');
    const result = await reloadFn();
    if (result.success && result.user) {
      // Update user state with reloaded data
      const currentUser = getCurrentUser();
      if (currentUser) {
        const userData = await getUserData(currentUser.uid);
        
        // CRITICAL: Sanitize subscription data to prevent raw Stripe objects
        if (userData && userData.subscription) {
          if (isRawStripeSubscription(userData.subscription)) {
            logger.error('❌ CRITICAL: Raw Stripe subscription object detected in reloadUser!');
            // Remove corrupted subscription data
            userData.subscription = {
              status: 'expired',
              planId: 'expired',
              stripeCustomerId: null,
              stripeSubscriptionId: null
            };
          } else {
            // Sanitize subscription to ensure no nested objects
            userData.subscription = sanitizeSubscription(userData.subscription) || {
              status: 'expired',
              planId: 'expired',
              stripeCustomerId: null,
              stripeSubscriptionId: null
            };
          }
        }
        
        setUser({
          uid: currentUser.uid,
          email: currentUser.email,
          emailVerified: currentUser.emailVerified,
          displayName: currentUser.displayName,
          ...userData
        });
      }
    }
    return result;
  };
  
  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated,
      signup,
      signin,
      logout,
      resetPassword,
      resendEmailVerification,
      reloadUser,
      uid
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

